import { useMemo, useState } from "react";
import PageHero from "@/components/base/PageHero";
import EpisodeCard from "@/components/base/EpisodeCard";
import Pagination from "@/components/base/Pagination";
import { episodes } from "@/mocks/episodes";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;

type SortKey = "number-asc" | "number-desc" | "date-asc" | "date-desc";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "number-asc", label: "集数正序" },
  { key: "number-desc", label: "集数倒序" },
  { key: "date-asc", label: "首播最早" },
  { key: "date-desc", label: "首播最新" },
];

const monthOptions = [
  { key: "all", label: "全部时间" },
  { key: "2024-03", label: "2024年3月" },
  { key: "2024-04", label: "2024年4月" },
  { key: "2024-05", label: "2024年5月" },
];

export default function EpisodesPage() {
  useSeo({
    title: "《年轻的母亲2》剧集列表 - 逐集剧情解析",
    description:
      "《年轻的母亲2》全季剧集列表，涵盖每一集的剧情简介、首播日期与内容重点，支持按集数与首播时间排序筛选，方便用户逐集浏览与追剧。",
    keywords: "年轻的母亲2剧集,剧集列表,逐集剧情,分集介绍,电视剧剧集",
  });

  const [sortKey, setSortKey] = useState<SortKey>("number-asc");
  const [month, setMonth] = useState("all");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = episodes.filter((ep) => (month === "all" ? true : ep.airDate.startsWith(month)));
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case "number-asc":
          return a.number - b.number;
        case "number-desc":
          return b.number - a.number;
        case "date-asc":
          return a.airDate.localeCompare(b.airDate);
        case "date-desc":
          return b.airDate.localeCompare(a.airDate);
        default:
          return 0;
      }
    });
    return list;
  }, [sortKey, month]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetPage = () => setPage(1);

  const FilterControls = (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
          排序方式
        </p>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => {
                setSortKey(opt.key);
                resetPage();
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm transition-colors ${
                sortKey === opt.key
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 font-label text-xs font-semibold uppercase tracking-wide text-foreground-500">
          按首播时间筛选
        </p>
        <div className="flex flex-wrap gap-2">
          {monthOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => {
                setMonth(opt.key);
                resetPage();
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm transition-colors ${
                month === opt.key
                  ? "bg-accent-500 font-semibold text-primary-950"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <PageHero
        eyebrow="剧集列表"
        title="《年轻的母亲2》剧集列表"
        desc="全季共 16 集，逐集梳理剧情重点与人物转折。可按集数正序/倒序或首播时间排序筛选，快速定位你想回顾的那一集。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "剧集列表" }]}
      />

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          {/* 侧栏筛选（桌面） */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-equalizer-line text-lg"></i>
                </span>
                筛选与排序
              </h2>
              {FilterControls}
              <div className="mt-5 border-t border-background-200 pt-4 text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 集
              </div>
            </div>
          </aside>

          {/* 移动端筛选入口 + 弹层 */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm font-semibold text-foreground-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-equalizer-line text-base"></i>
              </span>
              筛选与排序（共 {filtered.length} 集）
            </button>
          </div>

          <div>
            <div className="mb-4 hidden items-center justify-between lg:flex">
              <p className="text-sm text-foreground-600">
                第 {currentPage} / {totalPages} 页
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {pageItems.map((ep) => (
                <EpisodeCard key={ep.id} {...ep} />
              ))}
            </div>
            {pageItems.length === 0 && (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
                <p className="text-sm text-foreground-600">当前筛选条件下暂无剧集，试试切换时间范围。</p>
              </div>
            )}
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </div>

      {/* 移动端筛选弹层 */}
      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {FilterControls}
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 font-label text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 集结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}